import { Minus, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCartStore, type CartItem as CartItemType, resolveVariant, getEffectivePrice, getVariantLabel } from '@/stores/cart-store'
import type { Product, CustomInputDefinition } from '@/types/database'
import { cn } from '@/lib/utils'

/** Parse upload value which may be "url||originalFilename" or just a URL or plain filename */
function parseUploadValue(value: string): { url: string; fileName: string } {
  if (value.includes('||')) {
    const [url, ...rest] = value.split('||')
    return { url, fileName: rest.join('||') || 'Uploaded file' }
  }
  if (value.startsWith('http')) {
    return { url: value, fileName: 'Uploaded file' }
  }
  return { url: '', fileName: value }
}

interface CartItemProps {
  item: CartItemType
  product: Product
  className?: string
}

export function CartItem({ item, product, className }: CartItemProps) {
  const updateQuantity = useCartStore(state => state.updateQuantity)
  const removeItem = useCartStore(state => state.removeItem)

  // Resolve variant and calculate pricing
  const variant = resolveVariant(product, item.variantId)
  const effectivePrice = getEffectivePrice(product, variant)
  const variantLabel = getVariantLabel(variant)

  // Calculate price based on product status
  const discountPercent = product.status === 'featured'
    ? product.featured_discount_percent
    : product.archive_discount_percent

  const discountedPrice = effectivePrice * (1 - discountPercent / 100)
  const lineTotal = discountedPrice * item.quantity

  // Get thumbnail (variant image takes priority)
  const thumbnail = variant?.image_url || product.hero_image_url || product.video_thumbnail_url

  // Parse custom input definitions for labels
  const customInputDefs = (product.custom_inputs as unknown as CustomInputDefinition[]) || []
  const defMap = new Map(customInputDefs.map(d => [d.id, d]))

  const customEntries = item.customInputs
    ? Object.entries(item.customInputs).filter(([, v]) => v)
    : []

  return (
    <div className={cn('flex gap-3 py-3', className)}>
      {/* Thumbnail */}
      <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md bg-zinc-100">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={product.title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-zinc-400">
            <span className="text-2xl">1</span>
          </div>
        )}
        {discountPercent > 0 && (
          <span className="absolute top-0 left-0 rounded-br bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
            {discountPercent}% OFF
          </span>
        )}
      </div>

      {/* Details */}
      <div className="flex flex-1 flex-col justify-between">
        <div>
          <h4 className="line-clamp-1 text-sm font-medium text-zinc-900">
            {product.title}
          </h4>
          {variantLabel && (
            <span className="mt-1 inline-block rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">
              {variantLabel}
            </span>
          )}
          {customEntries.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {customEntries.map(([fieldId, value]) => {
                const def = defMap.get(fieldId)
                const label = def?.label || fieldId
                const isUploadField = def?.type === 'image_upload' || def?.type === 'file_upload'

                // Upload fields — label + clickable filename link
                if (isUploadField) {
                  const { url: fileUrl, fileName } = parseUploadValue(value)
                  return (
                    <p key={fieldId} className="text-[11px] text-zinc-500">
                      <span className="font-medium">{label}:</span>{' '}
                      {fileUrl ? (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-600 hover:underline"
                          title={fileName}
                        >
                          {fileName}
                        </a>
                      ) : (
                        <span>{fileName}</span>
                      )}
                    </p>
                  )
                }

                // Text fields — label: value
                return (
                  <p key={fieldId} className="text-[11px] text-zinc-500">
                    <span className="font-medium">{label}:</span> {value}
                  </p>
                )
              })}
            </div>
          )}
          <div className="mt-0.5 flex items-center gap-2 text-xs">
            {discountPercent > 0 && (
              <span className="text-zinc-400 line-through">
                ${effectivePrice.toFixed(2)}
              </span>
            )}
            <span className="font-medium text-red-600">
              ${discountedPrice.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Quantity controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-zinc-500 hover:text-zinc-900"
              onClick={() => updateQuantity(item.productId, item.variantId, item.quantity - 1)}
              aria-label="Decrease quantity"
            >
              <Minus className="size-3" />
            </Button>
            <span className="w-6 text-center text-sm font-medium text-zinc-900">
              {item.quantity}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-zinc-500 hover:text-zinc-900"
              onClick={() => updateQuantity(item.productId, item.variantId, item.quantity + 1)}
              aria-label="Increase quantity"
            >
              <Plus className="size-3" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-zinc-900">
              ${lineTotal.toFixed(2)}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-zinc-400 hover:text-red-600"
              onClick={() => removeItem(item.productId, item.variantId)}
              aria-label="Remove from cart"
            >
              <Trash2 className="size-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
